import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getImageThumbnailSrcFromImage, preventEventPropagation } from '@bingads-webui-campaign-react/smart-component-util';
import { CommandBarButton } from '@fluentui/react/lib/Button';

import { SelectImagesListView } from './select-images-list-view';
import { ImageItemView } from './image-item-view';

const IMAGE_WIDTH = 205;
const IMAGE_HEIGHT = 160;
const PAGE_HEIGHT = 400;

const getStyles = (_c, { palette = {} }) => ({
  itemContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  iconContainer: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    color: palette.themeDarkAlt,
    opacity: '0.95',
  },
  iconButton: {
    background: 'rgba(255, 255, 255, 0.83)',
    border: 'none',
    width: 32,
    height: 32,
    minWidth: 32,
    borderRadius: 99,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    // Hide the default chevron for menu
    '& span i:not(:first-child)': {
      display: 'none',
    },
  },
});

const UnStyledEditImagesView = ({
  items,
  i18n,
  onDelete,
  onReplace,
  classes,
  deleteOnly,
  imageHeight,
  imageWidth,
  pageHeight,
  horizontal,
  displayOnly,
  imageRetryLimit,
}) => {
  const renderIconContainer = useCallback((item, index) => {
    if (!deleteOnly) {
      const menuProps = {
        items: [
          {
            key: 'ReplaceImage',
            text: i18n.getString(_TL_('Replace image')),
            onClick: (e) => {
              preventEventPropagation(e);
              onReplace(item, index);
            },
            iconProps: { iconName: 'FileImage' },
          },
          {
            key: 'RemoveImage',
            text: i18n.getString(_TL_('Remove image')),
            onClick: (e) => {
              preventEventPropagation(e);
              onDelete(item, index);
            },
            iconProps: { iconName: 'Remove' },
          },
        ],
        calloutProps: {
          calloutMaxWidth: 140,
        },
      };

      return (
        <div className={classes.iconContainer}>
          <CommandBarButton
            iconProps={{ iconName: 'Edit' }}
            className={classnames(classes.iconButton, 'smart-page-gallery-edit-button')}
            ariaLabel={i18n.getString(_TL_('Edit image {{number}}'), { number: index + 1 })}
            menuProps={menuProps}
          />
        </div>
      );
    }

    return (
      <div className={classes.iconContainer}>
        <CommandBarButton
          onClick={() => onDelete(item, index)}
          iconProps={{ iconName: 'Cancel' }}
          className={classnames(classes.iconButton, 'smart-page-gallery-delete-button')}
          ariaLabel={i18n.getString(_TL_('Remove image {{number}}'), { number: index + 1 })}
        />
      </div>
    );
  }, [classes, i18n, onDelete, deleteOnly, onReplace]);

  const onRenderImageItem = useCallback((item, index) => (
    <div className={classes.itemContainer}>
      <ImageItemView
        imageId={item.imageId}
        resizeWidth={imageWidth}
        src={getImageThumbnailSrcFromImage(item)}
        retryLimit={imageRetryLimit}
      />
      {!displayOnly && renderIconContainer(item, index)}
    </div>
  ), [classes, renderIconContainer, imageWidth, displayOnly, imageRetryLimit]);

  return (
    <SelectImagesListView
      items={items}
      pageHeight={pageHeight}
      imageWidth={imageWidth}
      imageHeight={imageHeight}
      onRenderImageItem={onRenderImageItem}
      selectable={false}
      i18n={i18n}
      horizontal={horizontal}
    />
  );
};

UnStyledEditImagesView.propTypes = {
  classes: PropTypes.shape({
    itemContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  onDelete: PropTypes.func.isRequired,
  onReplace: PropTypes.func,
  deleteOnly: PropTypes.bool,
  imageHeight: PropTypes.number,
  imageWidth: PropTypes.number,
  pageHeight: PropTypes.number,
  imageRetryLimit: PropTypes.number,
  horizontal: PropTypes.bool,
  displayOnly: PropTypes.bool,
};

UnStyledEditImagesView.defaultProps = {
  items: [],
  deleteOnly: true,
  onReplace: _.noop,
  imageHeight: IMAGE_HEIGHT,
  imageWidth: IMAGE_WIDTH,
  pageHeight: PAGE_HEIGHT,
  horizontal: false,
  displayOnly: false,
  imageRetryLimit: 0,
};

export const EditImagesView = withDefaultStyles(
  UnStyledEditImagesView,
  getStyles
);
